import TurboCacheAware from "./turbo_cache_aware"

// Wraps DataTables (datatables.net-bs5) on a single <table>. Reused across
// customer_orders/child_details (one table) and customer_orders/screener
// (two tables, orders_table + payments_table) -- per-table variation
// (sort order, non-sortable columns, empty-state copy) is passed in as
// JSON via the options value; boilerplate that's identical everywhere
// (pagination icons, the dom layout string) lives here.
//
// Extends TurboCacheAware (see that file) so a page that DOES cache its
// snapshot (unlike screener/child_details, see disable_turbo_cache in
// customer_orders_controller.rb) still tears this down before caching.
//
// connect() checks `$.fn.dataTable.isDataTable(this.element)` up front for
// a different reason than it might look: DataTables' own init wraps/moves
// the table node in the DOM (a detach+reinsert), which Stimulus's
// MutationObserver sees as the element disconnecting and reconnecting --
// triggering a second, spurious connect() on a fresh controller instance
// while the first one is still mid-init. By the time that second connect()
// runs, the first one has already finished and isDataTable() is true, so
// this just retrieves the existing instance and returns instead of calling
// .DataTable({...}) again -- which would otherwise hit DataTables' own
// reinit guard and log a harmless but noisy "Cannot reinitialise" warning
// for no reason. (Destroying and recreating it here instead would be
// actively wrong: destroy() *also* detaches+reinserts the node, which
// would trigger another spurious reconnect, forever.)
//
// customSearch is opt-in (screener's two tables set it) for the shared
// cross-table substring-search extension: DataTables' search extension
// API is registered once globally, not per-table, so this guards against
// registering it twice when two datatable controllers connect on one page.
let customSearchRegistered = false
let errModeConfigured = false

export default class extends TurboCacheAware {
  static values = {
    options: Object,
    customSearch: Boolean,
  }

  connect() {
    // Deliberately set here, not at module top level: this file is
    // imported (via ./controllers) before ./jquery_global runs in
    // application.js, so a bare `$` reference in top-level module code
    // throws "$ is not defined" -- which, since esbuild concatenates
    // everything into one script, aborts all the code after it, including
    // jquery_global.js's own window.$/jQuery assignment. connect() only
    // ever runs once the whole bundle has already finished evaluating, so
    // it's always safe to reference $ here.
    if (!errModeConfigured) {
      errModeConfigured = true
      // DataTables' default errMode is 'alert' -- a blocking window.alert()
      // for any warning, including the benign "Cannot reinitialise
      // DataTable" notice that DataTables itself already recovers from
      // gracefully (see the note above on Stimulus's spurious reconnect
      // during init). A real, actionable DataTables error is rare enough
      // that console visibility is preferable to interrupting the user
      // with a popup for what's usually a harmless notice.
      $.fn.dataTable.ext.errMode = (settings, techNote, message) => console.warn(message)
    }

    if ($.fn.dataTable.isDataTable(this.element)) {
      this.table = $(this.element).DataTable()
      return
    }

    if (this.customSearchValue) {
      this.registerCustomSearch()
    }

    this.table = $(this.element).DataTable({
      dom: "<'d-flex justify-content-between align-items-baseline w-100 mb-3'<'small'f><'small'l>r>t<'d-flex justify-content-between align-items-center w-100 mt-3'<'small'i><'small'p>>",
      ...this.optionsValue,
      language: { ...this.defaultLanguage(), ...this.optionsValue.language },
    })

    if (this.customSearchValue) {
      $(`#${this.element.id}_filter input`)
        .off()
        .on("keyup", () => this.table.draw())
    }

    this.cleanupOnCache(() => this.table.destroy())
  }

  defaultLanguage() {
    return {
      // All four consistently Bootstrap Icons, matching the rest of the
      // page's icon set -- mixing a plain "<" with DataTables' own default
      // "«"/"»" for first/last reads as visually inconsistent (different
      // glyph weight/style), so first/last are overridden here too rather
      // than only previous/next.
      paginate: {
        first: "<i class='bi bi-chevron-bar-left'></i>",
        previous: "<i class='bi bi-chevron-left'></i>",
        next: "<i class='bi bi-chevron-right'></i>",
        last: "<i class='bi bi-chevron-bar-right'></i>",
      },
      search: "",
    }
  }

  registerCustomSearch() {
    if (customSearchRegistered) return
    customSearchRegistered = true

    // Matches all words in the search string in sequence, scoped to
    // whichever table's own search box triggered the redraw. DataTables
    // also invokes every registered ext.search function during earlier
    // internal passes (e.g. type detection) before `settings.nTable` is
    // necessarily populated -- tableId being unset just means "not enough
    // context to filter by yet", so don't filter anything out.
    $.fn.dataTable.ext.search.push((settings, data) => {
      const tableId = settings.nTable?.id
      if (!tableId) return true

      const searchVal = $(`#${tableId}_filter input`).val()?.trim()
      if (!searchVal) return true

      const rowText = data.join(" ").toLowerCase()
      return rowText.indexOf(searchVal.toLowerCase()) !== -1
    })
  }
}
